import { Lock, Unlock, ShieldAlert } from "lucide-react";
import { useNumberingOverride } from "@/hooks/useNumberingOverride";

/**
 * Drop-in replacement for the bare <input> on every voucher-number field.
 *
 * Behaviour:
 *   • Read-only by default — voucher numbers are owned by the system.
 *   • Users with `numbering_override.<module>=True` (or admin role) see a
 *     small "Unlock" button. Clicking it makes the input editable for the
 *     remainder of the form lifetime.
 *   • The displayed number is purely tentative — the backend assigns the
 *     final unique number at save time. The form should still send the
 *     current value (even unchanged) so the backend can rescue stale
 *     previews via the "stale preview rescue" path.
 *
 * Props:
 *   module    — backend numbering module key (e.g. "sales_invoice").
 *   value     — the current voucher-number string.
 *   onChange  — (string) → void
 *   editing   — local "unlocked" flag (lift state into the parent form so
 *               the unlock survives re-renders).
 *   onEditingChange — (bool) → void.  Defaults to internal state if omitted.
 *   testidPrefix — for data-testid attributes.
 *   placeholder — passed through.
 *   className — extra classes (size, focus ring etc.)
 */
import { useState } from "react";

export function VoucherNumberInput({
  module,
  value,
  onChange,
  editing,
  onEditingChange,
  testidPrefix = "voucher",
  placeholder = "Auto-generated on save",
  className = "",
  inputClassName = "",
  disabled = false,
}) {
  const { canOverride, ready } = useNumberingOverride();
  const [localEditing, setLocalEditing] = useState(false);
  const isControlled = onEditingChange != null;
  const isEditing = isControlled ? !!editing : localEditing;
  const setEditing = (v) => (isControlled ? onEditingChange(v) : setLocalEditing(v));

  const allowed = ready && canOverride(module);
  const readOnly = disabled || !isEditing;

  return (
    <div className={`flex items-stretch ${className}`} data-testid={`${testidPrefix}-wrap`}>
      <input
        type="text"
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        readOnly={readOnly}
        placeholder={placeholder}
        data-testid={`${testidPrefix}-input`}
        title={readOnly
          ? "Voucher numbers are auto-generated. Ask an admin to enable 'Numbering Override' for this module to edit."
          : "Manual override active — your value will be validated for uniqueness on save."}
        className={`flex-1 px-2 py-1.5 border text-xs focus:outline-none ${
          readOnly
            ? "bg-slate-100 text-slate-600 border-slate-200 cursor-default"
            : "bg-amber-50 text-slate-900 border-amber-400 focus:border-amber-600"
        } ${inputClassName}`}
      />
      {allowed && !disabled && (
        <button
          type="button"
          onClick={() => setEditing(!isEditing)}
          data-testid={`${testidPrefix}-toggle`}
          title={isEditing ? "Lock — re-enable auto numbering" : "Unlock — override voucher number"}
          className={`px-2 border border-l-0 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 ${
            isEditing
              ? "bg-amber-500 hover:bg-amber-600 text-white border-amber-500"
              : "bg-white hover:bg-slate-50 text-slate-500 border-slate-200 hover:text-amber-700 hover:border-amber-300"
          }`}>
          {isEditing ? <Unlock size={11}/> : <Lock size={11}/>}
        </button>
      )}
      {!allowed && ready && (
        <span
          title="Numbering override permission required (see Settings → Users)"
          className="px-2 border border-l-0 border-slate-200 bg-slate-50 text-slate-300 flex items-center"
          data-testid={`${testidPrefix}-locked-badge`}>
          <ShieldAlert size={11}/>
        </span>
      )}
    </div>
  );
}
